/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  notification,
  Radio,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CarOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  MailOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import OrderEditModal from "./components/OrderEditModal";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import moment from "moment/min/moment-with-locales";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import utils from "utils";
import { Trans, useTranslation } from "react-i18next";
import { CURRENCY, DELIVERY_DATE_WARNING_DAYS, DELIVERY_OPTIONS } from "constants/DeliveryConstants";
import { vceFirestore } from "../../../configs/FirebaseConfig";
import { sendShipmentToTrunkrs } from "../../../functions/api/trunkrs";
import { createSooCoolShipments, fetchSooCoolLabelPdf } from "../../../functions/api/soocool";
import {
  formatSooCoolGoodWeight,
  formatSooCoolTimeWindow,
  getSooCoolCompletionProblem,
  getSooCoolDeliveredAtNeighbours,
  getSooCoolDeliveryTask,
  getSooCoolTrackAndTraceMail,
  getSooCoolNeighbourHouseNumber,
  getSooCoolPlannedTimeWindow,
  getSooCoolTaskStateColor,
  getSooCoolTrackAndTraceLink,
  getSooCoolTransportRequirementColor,
} from "../../../functions/soocool";
import { useCategories, useOrders, usePickRoute, useProducts } from "../../../data/hooks";
import { buildPickRouteIndex } from "../../../functions/pickRoute";
import Text from "antd/lib/typography/Text";
import {
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_CREATED,
  ORDER_STATUS_KLARNA_AUTHORIZED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_SHIPPED,
} from "../../../constants/OrderConstant";
import { useReactToPrint } from "react-to-print";
import { generateInvoice, getPickListData, getSendListData } from "../../../functions/orders";
import { getChillBillExportData } from "../../../functions/excel";
import PickListTable from "../../../components/shared-components/Table/PickListTable";
import { formatInvoiceFileName, formatOrderDate, formatPrice, getPaymentStatus } from "../../../functions/formatters";
import _ from "lodash";
import locale from "antd/es/date-picker/locale/nl_NL";
import { createDeliveryOrder } from "functions/api/optimoroute";
import { sendTrustpilotReviewInvitations } from "../../../functions/api/trustpilot";
import styled from "styled-components";
import { downloadFile, isMobile } from "../../../functions/browser";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import OrderListTable from "../../../components/shared-components/Table/OrderListTable";
import PackingSlip from "../../../components/shared-components/Table/PackingSlip";
import SendLabel from "../../../components/shared-components/Table/SendLabel";
import ExcelExport from "../../../components/shared-components/ExcelExport";
import * as dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const CARRIER_TRUNKRS = "trunkrs";
const CARRIER_SOOCOOL = "soocool";

/**
 * OptimoRoute now writes `optimoRouteExportedAt` on the order (see `functions/api/optimoroute.js`),
 * so it is a real booking like Trunkrs and SooCool. It used to write only `orderStatus = SHIPPED`,
 * which meant it could not be told apart from any other carrier and could not block one.
 *
 * Orders exported BEFORE that change carry no marker, only SHIPPED, and never get the field
 * retroactively. `isLegacyOptimoRouteBooking` recognises them so they block a second booking all
 * the same; `exportStats` keeps flagging them, because there the attribution is inference rather
 * than something the order itself records.
 */
const CARRIER_OPTIMOROUTE = "optimoroute";

const CARRIER_LABELS = {
  [CARRIER_TRUNKRS]: "Trunkrs",
  [CARRIER_SOOCOOL]: "SooCool",
  [CARRIER_OPTIMOROUTE]: "OptimoRoute",
};

/** The order carriers are listed in inside a stats row. */
const CARRIER_ORDER = [CARRIER_TRUNKRS, CARRIER_SOOCOOL, CARRIER_OPTIMOROUTE];

/**
 * Grouping key for a SooCool skip that arrives without a `reason`, i.e. from an api deploy that
 * predates the field. Those orders keep the original, carrier-neutral wording.
 */
const SKIP_REASON_MISSING = "__no_reason__";

/**
 * A VCE regional order that is SHIPPED but carries no carrier marker at all: exported to
 * OptimoRoute before `optimoRouteExportedAt` existed (see functions/api/optimoroute.js).
 *
 * Historical orders never get that field retroactively, so without this they read as "still free"
 * and can be handed to SooCool although one of our own vans already took the parcel - a second
 * pickup with nothing on the order to show for it, which is exactly the invisible double booking
 * the marker was added to prevent.
 *
 * Deliberately narrow. Only VCE regional, where OptimoRoute is the single export that could have
 * set SHIPPED. A national order that is SHIPPED without a marker is the ChillBill case and must
 * stay bookable, because moving national - Saturday included - to SooCool is the point of the
 * migration.
 */
const isLegacyOptimoRouteBooking = (order) =>
  order?.orderStatus === ORDER_STATUS_SHIPPED &&
  order?.shopSource === "VCE" &&
  order?.landelijk === false &&
  // A pickup order can carry `landelijk: false` too, and nothing ever books a carrier for it.
  order?.delivery?.type === DELIVERY_OPTIONS.SEND &&
  !order?.trunkrsNumber &&
  !order?.soocool?.orderId &&
  !order?.optimoRouteExportedAt;

/**
 * Which carrier an order has ALREADY been booked with, or null when it is still free.
 *
 * This exists because Trunkrs and SooCool are both live during the transition
 * (planning/soocool-koppeling.md, besluit 1) while `getOrdersReadyToShip()` deliberately keeps
 * returning orders that are already SHIPPED. Without this check, running Export -> SooCool and
 * then Export -> Trunkrs on the same day books every national order with BOTH carriers: two
 * pickups, two sets of labels, double billing. Both export paths ask this one helper, so the two
 * conditions can never drift apart again.
 *
 * ChillBill is knowingly missing here: see the note at `handleChillBillExport`.
 */
const getCarrierBooking = (order) => {
  if (order?.trunkrsNumber) return CARRIER_TRUNKRS;
  if (order?.soocool?.orderId) return CARRIER_SOOCOOL;
  if (order?.optimoRouteExportedAt) return CARRIER_OPTIMOROUTE;
  // Last, so a real marker always wins: this one is inferred from the status.
  if (isLegacyOptimoRouteBooking(order)) return CARRIER_OPTIMOROUTE;
  return null;
};

/**
 * Split a set of orders into the ones still free to book and the ones a different carrier already
 * has, grouped per carrier so the caller can name that carrier in the notification.
 *
 * Every export path runs its candidates through this, so "SooCool skips what OptimoRoute has" and
 * "OptimoRoute skips what SooCool has" are one rule instead of two that can drift.
 */
const splitByExistingBooking = (orders, ownCarrier) => {
  const free = [];
  const blockedByCarrier = {};

  orders.forEach((order) => {
    const booked = getCarrierBooking(order);
    // Already booked with ourselves is not a conflict: the per-carrier export decides for itself
    // whether that is a skip (SooCool, via the api) or a re-send.
    if (!booked || booked === ownCarrier) {
      free.push(order);
      return;
    }
    if (!blockedByCarrier[booked]) blockedByCarrier[booked] = [];
    blockedByCarrier[booked].push(order);
  });

  return { free, blockedByCarrier };
};

// Single DatePicker - no need for RangePicker anymore
// const ExcelFile = ReactExport.ExcelFile;
// const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;

const Orders = () => {
  const promiseResolveRef = useRef(null);

  const orderListRef = useRef();
  const pickListRef = useRef();
  const packingSlipRef = useRef();
  const packingSlipRefLandelijk = useRef();
  const packingSlipRefRegionaal = useRef();
  const packingSlipRefVceLandelijk = useRef();
  const packingSlipRefHvoLandelijk = useRef();
  const combinedPackingSendRef = useRef();
  const combinedPackingSendRefLandelijk = useRef();
  const combinedPackingSendRefRegionaal = useRef();
  const combinedPackingSendRefVceLandelijk = useRef();
  const combinedPackingSendRefHvoLandelijk = useRef();
  const sendListRef = useRef();
  const sendListRefLandelijk = useRef();
  const sendListRefRegionaal = useRef();
  const sendListRefVceLandelijk = useRef();
  const sendListRefHvoLandelijk = useRef();

  // Single date for filtering (default to today)
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const deliveryDateStr = useMemo(() => selectedDate.format(DATE_FORMAT_DD_MM_YYYY), [selectedDate]);

  // Use custom hooks for data fetching with selected date
  const {
    orders,
    isLoading: ordersLoading,
    updateOrder,
    findOrderById: findOrderByIdHook,
    refreshOrders,
  } = useOrders({ realtime: true, deliveryDate: deliveryDateStr });
  const { products, isLoading: productsLoading } = useProducts();
  const { categories, isLoading: categoriesLoading } = useCategories();
  const { entries: pickRouteEntries } = usePickRoute();
  // The walking order every packing slip is sorted by. Built once per render of the page
  // rather than per slip, since a print run can cover a couple of hundred orders.
  const pickRouteIndex = useMemo(() => buildPickRouteIndex(pickRouteEntries), [pickRouteEntries]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [order, setOrder] = useState(null);
  const { t } = useTranslation();
  const hasWeightProperty = order ? order.products.some((item) => item.variation && item.variation.weight) : false;
  const [datePickerForm] = Form.useForm();
  const [editOrderModalVisible, setEditOrderModalVisible] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [findingById, setFindingById] = useState(false);

  // Combined loading state
  const dataLoading = ordersLoading || productsLoading || categoriesLoading;

  moment.locale("nl");

  // Use useMemo instead of useState + useEffect to prevent infinite loops
  const filteredList = useMemo(() => {
    if (searchText) {
      return utils.wildCardSearchOnOrder(orders, searchText, null);
    }
    return orders;
  }, [orders, searchText]);

  // We watch for the state to change here, and for the Promise resolve to be available
  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const enrichOrdersWithProductData = (orders, products) => {
    // Early return if no products data available
    if (!products || products.length === 0) {
      return orders;
    }

    // Create a lookup map with shopSource-specific keys for O(1) product access
    // Key format: "productId|shopSource" to handle same products in different shops
    const productMap = products.reduce((map, product) => {
      const key = `${product.id}|${product.shopSource}`;
      map[key] = product;
      return map;
    }, {});

    // Enrich each order's products with data from the products collection
    return orders.map((order) => ({
      ...order,
      products: order.products.map((orderProduct) => {
        // Look up product using order's shopSource to get the correct variant
        const productKey = `${orderProduct.productId}|${order.shopSource}`;
        const productData = productMap[productKey];

        const productItems =
          productData?.productItems?.length > 0
            ? productData.productItems.map((p) => {
                // Also look up sub-products with shopSource
                const subProductKey = `${p.productId}|${order.shopSource}`;
                const foundProduct = productMap[subProductKey];
                if (!foundProduct) return null;
                return {
                  ...p,
                  name: foundProduct.name,
                  frozen: foundProduct?.frozen || false,
                  purchased: foundProduct?.purchased || 0,
                };
              })
            : [];
        return {
          ...orderProduct,
          frozen: productData?.frozen || false,
          purchased: productData?.purchased || 0,
          productItems,
        };
      }),
    }));
  };

  const showDrawer = (row) => {
    setVisible(true);
    setOrder(row);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const formatShippingMethod = (value) => {
    if (value === DELIVERY_OPTIONS.SEND) return t("shipping");
    if (value === DELIVERY_OPTIONS.PICKUP) return t("pickup");
  };

  const getPaymentStatusColor = (status) => {
    if (status === ORDER_STATUS_PAID) return "geekblue";
    else if (status === ORDER_STATUS_SHIPPED) return "lime";
    else if (status === ORDER_STATUS_COMPLETED) return "cyan";
    else if (status === ORDER_STATUS_CREATED) return "red";
    else if (status === ORDER_STATUS_KLARNA_AUTHORIZED) return "orange";
  };

  const getShopBadgeColor = (order) => {
    if (order.shopSource === "VCE") {
      if (order.delivery?.type === DELIVERY_OPTIONS.PICKUP) {
        return "orange";
      } else if (order.delivery?.type === DELIVERY_OPTIONS.SEND) {
        return order.landelijk === false ? "green" : "blue";
      }
      return "green";
    }
    return "purple";
  };

  const handleManualPrint = (functionName) => {
    setIsPrinting(true);
    setTimeout(() => {
      functionName();
    }, 1000);
  };

  const handleOrderListPrint = useReactToPrint({
    contentRef: orderListRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePickListPrint = useReactToPrint({
    contentRef: pickListRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePackingSlipPrint = useReactToPrint({
    contentRef: packingSlipRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });
  //
  const handleSendListPrint = useReactToPrint({
    contentRef: sendListRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleCombinedPackingSendPrint = useReactToPrint({
    contentRef: combinedPackingSendRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePackingSlipPrintLandelijk = useReactToPrint({
    contentRef: packingSlipRefLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePackingSlipPrintRegionaal = useReactToPrint({
    contentRef: packingSlipRefRegionaal,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePackingSlipPrintVceLandelijk = useReactToPrint({
    contentRef: packingSlipRefVceLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handlePackingSlipPrintHvoLandelijk = useReactToPrint({
    contentRef: packingSlipRefHvoLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleCombinedPackingSendPrintLandelijk = useReactToPrint({
    contentRef: combinedPackingSendRefLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleCombinedPackingSendPrintRegionaal = useReactToPrint({
    contentRef: combinedPackingSendRefRegionaal,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleCombinedPackingSendPrintVceLandelijk = useReactToPrint({
    contentRef: combinedPackingSendRefVceLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleCombinedPackingSendPrintHvoLandelijk = useReactToPrint({
    contentRef: combinedPackingSendRefHvoLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleSendListPrintLandelijk = useReactToPrint({
    contentRef: sendListRefLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleSendListPrintRegionaal = useReactToPrint({
    contentRef: sendListRefRegionaal,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleSendListPrintVceLandelijk = useReactToPrint({
    contentRef: sendListRefVceLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleSendListPrintHvoLandelijk = useReactToPrint({
    contentRef: sendListRefHvoLandelijk,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  /**
   * Tell staff which orders were held back because another carrier already has them, and who
   * that carrier is. One notification per blocking carrier, so "al via Trunkrs verstuurd" never
   * gets folded into "al via OptimoRoute gepland" - they need different follow-up.
   */
  const notifyBlockedByOtherCarrier = (blockedByCarrier, ownCarrier) => {
    Object.entries(blockedByCarrier).forEach(([carrier, blockedOrders]) => {
      if (blockedOrders.length === 0) return;
      // Orders held back on inference rather than on a marker (`isLegacyOptimoRouteBooking`) are
      // named separately: they are the ones where "al via OptimoRoute verstuurd" is our best
      // reading of an old SHIPPED status, not something the order records.
      const inferredOrders = blockedOrders.filter(isLegacyOptimoRouteBooking);
      notification.warning({
        message: t("carrier.blocked_title", {
          count: blockedOrders.length,
          carrier: CARRIER_LABELS[carrier] || carrier,
        }),
        description: (
          <div>
            <div>
              {t("carrier.blocked_description", {
                carrier: CARRIER_LABELS[carrier] || carrier,
                ownCarrier: CARRIER_LABELS[ownCarrier] || ownCarrier,
                orders: formatOrderNumberList(blockedOrders.map((order) => order.orderId)),
              })}
            </div>
            {inferredOrders.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {t("carrier.blocked_inferred", {
                  ownCarrier: CARRIER_LABELS[ownCarrier] || ownCarrier,
                  orders: formatOrderNumberList(inferredOrders.map((order) => order.orderId)),
                })}
              </div>
            )}
          </div>
        ),
        duration: 8,
      });
    });
  };

  const handleOptimoRouteExport = async () => {
    // Only VCE orders with delivery.type === "send" and landelijk === false (regional)
    const allOrders = getOrdersReadyToShip();
    const regionalOrders = allOrders.filter((order) => order.shopSource === "VCE" && order.landelijk === false);

    // Regional orders can now also go out via SooCool, so an order that already has a SooCool
    // shipment must not be planned into an OptimoRoute route as well.
    const { free: ordersToExport, blockedByCarrier } = splitByExistingBooking(regionalOrders, CARRIER_OPTIMOROUTE);
    notifyBlockedByOtherCarrier(blockedByCarrier, CARRIER_OPTIMOROUTE);

    if (ordersToExport.length === 0) {
      if (Object.keys(blockedByCarrier).length === 0) {
        message.warning("Geen VCE regionale verzendbestellingen geselecteerd");
      }
      return;
    }

    setLoading(true);
    const { exported } = await createDeliveryOrder(ordersToExport, vceFirestore).catch(() => ({
      exported: [],
      failed: ordersToExport,
    }));
    setLoading(false);

    // Only the orders OptimoRoute actually accepted get a review invitation - a failed order has
    // no route and is going to be exported again.
    await sendTrustpilotReviewInvitations(exported, updateOrder);

    // Refresh orders to show updated status immediately
    await refreshOrders();
  };

  /**
   * Ask before handing Tuesday-to-Saturday orders to Trunkrs. Resolves to true only on an
   * explicit "toch versturen" - closing the dialog in any other way cancels the export.
   */
  const confirmTrunkrsOutsideMonday = (notOnMonday) =>
    new Promise((resolve) => {
      Modal.confirm({
        title: `${notOnMonday.length} ${notOnMonday.length === 1 ? "bestelling wordt" : "bestellingen worden"} niet op maandag bezorgd`,
        content: (
          <>
            <p>
              Trunkrs is de maandagvervoerder. Dinsdag t/m zaterdag gaat via SooCool; die
              {notOnMonday.length === 1 ? " bestelling hoort" : " bestellingen horen"} dus bij de
              SooCool-export.
            </p>
            <p>{formatOrderNumberList(notOnMonday.map((order) => order.orderId))}</p>
            <p>Alleen doorzetten als SooCool vandaag niet kan rijden.</p>
          </>
        ),
        okText: "Toch via Trunkrs versturen",
        okButtonProps: { danger: true },
        cancelText: "Annuleren",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleTrunkrsExport = async () => {
    // Since SooCool took over, Trunkrs is the MONDAY carrier (owner's decision 2026-10-07, see
    // planning/soocool-koppeling.md): SooCool picks Monday deliveries up on the Friday before,
    // and the remaining Trunkrs contract wants 50 orders a week. On Monday Trunkrs carries EVERY
    // send order of both shops - regional included, which is why the old `landelijk === true`
    // filter is gone: a West-Friesland order for Monday would otherwise fit no carrier at all,
    // because the api refuses Monday deliveries for SooCool. Saturday stays excluded: Trunkrs
    // does not deliver then.
    const allOrders = getOrdersReadyToShip();
    const ordersToExport = allOrders.filter(
      (order) =>
        (order.shopSource === "VCE" || order.shopSource === "HVO") &&
        dayjs(order.delivery.date, "DD-MM-YYYY").day() !== 6,
    );

    if (ordersToExport.length === 0) {
      message.warning("Geen verzendbestellingen geselecteerd");
      return;
    }

    // Tuesday to Saturday belongs to SooCool. Asked, not blocked: when SooCool is down, Trunkrs
    // has to stay usable as the way out - but nobody should do that by accident.
    const notOnMonday = ordersToExport.filter(
      (order) => dayjs(order.delivery.date, "DD-MM-YYYY").day() !== 1,
    );
    if (notOnMonday.length > 0 && !(await confirmTrunkrsOutsideMonday(notOnMonday))) {
      return;
    }

    setLoading(true);
    const skippedOrders = [];
    const skippedSooCoolOrders = [];
    const skippedOtherCarrierOrders = [];
    // The orders that get the "onderweg vandaag" (picked_up) mail at the end. Collected here,
    // per branch, instead of reusing `ordersToExport` - that set also holds every order this
    // loop skips, so mailing from it tells customers their parcel left when it did not.
    //
    // Careful: the pushes below reproduce TODAY's behaviour on purpose. An order already at
    // Trunkrs, and an order whose Trunkrs POST threw, are still mailed - that is a pre-existing
    // bug, logged in planning/soocool-koppeling.md ("Bekende bugs in de bestaande export") and
    // out of scope for the SooCool story. Fixing it later means deleting those two pushes.
    // Orders skipped because SooCool already has them are the case this story owns: they are
    // deliberately NOT pushed.
    const ordersToMail = [];
    let exportedCount = 0;

    for (const row of ordersToExport) {
      const isLastRow = ordersToExport.indexOf(row) === ordersToExport.length - 1;
      const bookedCarrier = getCarrierBooking(row);

      if (bookedCarrier === CARRIER_TRUNKRS) {
        skippedOrders.push(row.orderId);
        // Pre-existing: an already-booked order is mailed anyway. See the note at `ordersToMail`.
        ordersToMail.push(row);
        if (isLastRow) {
          setLoading(false);
        }
        continue;
      }

      // Already out with SooCool: booking it at Trunkrs as well means two pickups and two
      // sets of labels for the same order. No picked_up mail either - Trunkrs is not carrying
      // this parcel, so "onderweg vandaag" via Trunkrs would be plainly wrong.
      if (bookedCarrier === CARRIER_SOOCOOL) {
        skippedSooCoolOrders.push(row.orderId);
        if (isLastRow) {
          setLoading(false);
        }
        continue;
      }

      // Any other carrier that already has this order - in practice OptimoRoute. Normally it
      // cannot happen (Trunkrs is national, OptimoRoute regional), but it became reachable the
      // moment staff can correct a postcode across the regional border AFTER the order was
      // exported: `OrderEditModal` recomputes `landelijk`, and the order lands in this list
      // carrying an OptimoRoute booking.
      if (bookedCarrier) {
        // The whole order, not just its number: `notifyBlockedByOtherCarrier` needs the order
        // itself to tell a recorded booking apart from one inferred from the status.
        skippedOtherCarrierOrders.push(row);
        if (isLastRow) {
          setLoading(false);
        }
        continue;
      }

      const delivery = row.customer.address.delivery;
      let weight = 0;
      row.products.forEach((product) => {
        const productWeight =
          !!product.variation && !!product.variation.weight ? product.quantity * parseInt(product.variation.weight) : 0;
        weight += productWeight;
      });
      // Filter out gift cards and products without productId
      const elligibleProducts = row.products.filter((product) => !!product.productId && !product.giftCard);
      const object = {
        orderReference: row.orderId,
        sender: {
          name: row.shopSource === "VCE" ? "Het Vleescentrum" : "Halalvlees Online",
          companyName: row.shopSource === "VCE" ? "Het Vleescentrum" : "Halalvlees Online",
          emailAddress: row.shopSource === "VCE" ? "info@hetvleescentrum.nl" : "info@halalvlees-online.nl",
          address: "De Hoek 91A",
          postalCode: "1601MR",
          city: "Enkhuizen",
          country: "NL",
        },
        recipient: {
          name: row.customer.firstName + " " + row.customer.lastName,
          companyName: row.customer.companyName || "",
          emailAddress: row.customer.email,
          address: delivery.street + " " + delivery.houseNumber + (!!delivery.addition ? " " + delivery.addition : ""),
          postalCode: delivery.zipCode,
          city: delivery.city,
          country: delivery.country || "NL",
        },
        parcel: [
          {
            description: row.orderId,
            weight: {
              value: weight,
              unit: "g",
            },
            contents: elligibleProducts.map((product) => {
              return {
                name: product.name,
                reference: product.productId,
              };
            }),
          },
        ],
        service: "SAME_DAY",
      };
      try {
        const trunkrsNumber = await sendShipmentToTrunkrs(object);
        let firestoreObject = {
          orderStatus: ORDER_STATUS_SHIPPED,
        };
        if (!!trunkrsNumber) firestoreObject.trunkrsNumber = trunkrsNumber;

        // Use updateOrder hook for consistent updates with optimistic UI
        await updateOrder(row, firestoreObject);
        exportedCount++;
        ordersToMail.push(row);

        if (isLastRow) {
          setLoading(false);
        }
      } catch (err) {
        notification.error({
          message: t("notifications.error_saving"),
          description: row.orderId + ": " + err.toString().replace("Error: ", ""),
          duration: 0,
        });
        // Pre-existing: a failed export is mailed anyway. See the note at `ordersToMail`.
        ordersToMail.push(row);
        setLoading(false);
      }
    }

    setLoading(false);

    if (exportedCount > 0) {
      message.success(
        `${exportedCount} ${exportedCount === 1 ? "bestelling" : "bestellingen"} geëxporteerd naar Trunkrs`,
      );
    }

    if (skippedOrders.length > 0) {
      notification.warning({
        message: `${skippedOrders.length} ${skippedOrders.length === 1 ? "bestelling" : "bestellingen"} overgeslagen`,
        description: `De volgende ${skippedOrders.length === 1 ? "bestelling was" : "bestellingen waren"} al naar Trunkrs verzonden: ${skippedOrders.join(", ")}`,
        duration: 8,
      });
    }

    if (skippedSooCoolOrders.length > 0) {
      notification.warning({
        message: `${skippedSooCoolOrders.length} ${skippedSooCoolOrders.length === 1 ? "bestelling" : "bestellingen"} overgeslagen`,
        description: `De volgende ${skippedSooCoolOrders.length === 1 ? "bestelling is" : "bestellingen zijn"} al via SooCool verstuurd en ${skippedSooCoolOrders.length === 1 ? "is" : "zijn"} daarom niet ook bij Trunkrs aangemeld: ${formatOrderNumberList(skippedSooCoolOrders)}`,
        duration: 8,
      });
    }

    if (skippedOtherCarrierOrders.length > 0) {
      notifyBlockedByOtherCarrier(_.groupBy(skippedOtherCarrierOrders, getCarrierBooking), CARRIER_TRUNKRS);
    }

    if (process.env.REACT_APP_ENVIRONMENT === "production") {
      // Send orders to API to send a mail to the customer
      const vceOrders = [];
      const hvoOrders = [];
      ordersToMail.forEach((order) => {
        const object = {
          orderId: order.orderId,
          email: order.customer.email,
          firstName: order.customer.firstName,
          lastName: order.customer.lastName,
        };
        if (order.shopSource === "VCE") vceOrders.push(object);
        else hvoOrders.push(object);
      });

      const endpoint = "/api/email/order/picked_up";
      // Send mail to Vleescentrum customers
      if (vceOrders.length > 0) {
        axios.post(process.env.REACT_APP_VCE_API_URL + endpoint, vceOrders).catch((err) => {
          console.log(err);
        });
      }

      // Send mail to Halalvlees Online customers
      if (hvoOrders.length > 0) {
        axios.post(process.env.REACT_APP_HVO_API_URL + endpoint, hvoOrders).catch((err) => {
          console.log(err);
        });
      }
    }

    // Refresh orders to show updated status immediately
    await refreshOrders();
  };

  const getOrdersReadyToShip = () => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    const filteredOrders = _.filter(ordersToFilter, function (order) {
      return (
        [ORDER_STATUS_PAID, ORDER_STATUS_KLARNA_AUTHORIZED, ORDER_STATUS_SHIPPED].includes(order.orderStatus) &&
        order.delivery.type === DELIVERY_OPTIONS.SEND
      );
    });

    // Remove all gift card only orders
    const filteredByGiftCard = _.filter(filteredOrders, function (order) {
      return !order.containsOnlyGiftCards;
    });
    // Order by date ascending
    return _.orderBy(filteredByGiftCard, ["createdAt"], ["asc"]);
  };

  const handleChillBillExport = () => {
    // Note: a ChillBill export only writes an Excel file and leaves NO marker on the order, so
    // unlike Trunkrs (`trunkrsNumber`) and SooCool (`soocool.orderId`) it cannot be cross-checked
    // by `getCarrierBooking` - a ChillBill/SooCool double booking stays invisible to this screen.

    // Check if Saturday is selected (Saturday = 6 in dayjs, where 0=Sunday)
    const isSaturdaySelected = selectedDate.day() === 6;

    if (!isSaturdaySelected) {
      message.warning(
        "Chill Bill export is alleen beschikbaar voor zaterdag leveringen. Selecteer een zaterdag in de kalender.",
      );
      return;
    }

    // Filter for national orders (VCE landelijk + HVO) with delivery.type === 'send'
    // Include shipped orders for Chill Bill export (it's just an Excel export)
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const allOrders = _.filter(ordersToFilter, function (order) {
      return (
        [ORDER_STATUS_PAID, ORDER_STATUS_KLARNA_AUTHORIZED, ORDER_STATUS_SHIPPED].includes(order.orderStatus) &&
        order.delivery.type === DELIVERY_OPTIONS.SEND
      );
    });
    const chillBillOrders = allOrders.filter(
      (order) => (order.shopSource === "VCE" && order.landelijk === true) || order.shopSource === "HVO",
    );

    if (chillBillOrders.length === 0) {
      message.warning("Geen landelijke verzendbestellingen gevonden voor Chill Bill export");
      return;
    }

    try {
      // Get the export data
      const exportData = getChillBillExportData(chillBillOrders);
      const fileName = `Chill Bill levering ${dayjs().format("YYYYMMDD")}`;

      // Create Excel workbook
      const workbook = XLSX.utils.book_new();

      // Process the data (same logic as ExcelExport component)
      exportData.forEach((sheet, sheetIndex) => {
        // Create headers from columns
        const headers = sheet.columns.map((col) => col.title);

        // Transform the data to include headers
        const excelData = [headers, ...sheet.data.map((row) => row.map((cell) => cell.value))];

        // Create worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(excelData);

        // Add worksheet to workbook with sheet name if provided
        const sheetName = sheet.sheetName || `Sheet${sheetIndex + 1}`;
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      // Generate and save the file
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(blob, `${fileName}.xlsx`);

      message.success(`${chillBillOrders.length} bestellingen geëxporteerd voor Chill Bill`);
    } catch (error) {
      console.error("Error exporting Chill Bill Excel:", error);
      message.error("Fout bij het exporteren van Chill Bill Excel");
    }
  };

  /**
   * A readable order-number list for a notification: a full day can hold a hundred orders and a
   * notification that long is unreadable, so only the first few are named.
   */
  const formatOrderNumberList = (orderNumbers, maxShown = 15) => {
    if (orderNumbers.length <= maxShown) return orderNumbers.join(", ");
    return `${orderNumbers.slice(0, maxShown).join(", ")} (+ ${orderNumbers.length - maxShown} meer)`;
  };

  /**
   * The ONE place that decides which orders go out via SooCool.
   *
   * SooCool replaces Trunkrs (landelijk ma-vr), ChillBill (landelijk zaterdag) AND the regional
   * runs that used to go out via OptimoRoute and our own vans - see planning/soocool-koppeling.md,
   * besluit 1. Regional was the last of the three to move over, so the scope is now simply every
   * send order of both shops: VCE regionaal + VCE landelijk + HVO. There is deliberately NO
   * Saturday exclusion here (Trunkrs has one because ChillBill takes Saturday); SooCool delivers
   * on Saturday itself.
   *
   * Orders that already went out with another carrier are held back (see `getCarrierBooking`) and
   * handed back per carrier, so the caller can say WHY they are missing instead of dropping them
   * quietly. Since regional came into scope that includes OptimoRoute, not just Trunkrs.
   */
  const getSooCoolEligibleOrders = () => {
    const ordersInScope = getOrdersReadyToShip().filter(
      (order) => order.shopSource === "VCE" || order.shopSource === "HVO",
    );

    const { free, blockedByCarrier } = splitByExistingBooking(ordersInScope, CARRIER_SOOCOOL);
    return { eligible: free, blockedByCarrier };
  };

  /**
   * Orders in the current scope that already have a SooCool shipment, i.e. the ones whose label
   * must come from SooCool instead of the self-made A6 in `SendLabel.js`.
   */
  const getOrdersWithSooCoolShipment = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    return _.orderBy(
      ordersToFilter.filter((order) => !!order.soocool?.orderId),
      ["createdAt"],
      ["asc"],
    );
  };

  const handleSooCoolExport = async () => {
    const { eligible: ordersToExport, blockedByCarrier } = getSooCoolEligibleOrders();

    notifyBlockedByOtherCarrier(blockedByCarrier, CARRIER_SOOCOOL);

    if (ordersToExport.length === 0) {
      // Nothing left to send: only say "no orders" when that is not already explained above.
      if (Object.keys(blockedByCarrier).length === 0) message.warning(t("soocool.no_orders"));
      return;
    }

    setLoading(true);

    // One bulk call per shop: each shop runs its own api deploy against its own Firestore, so
    // the VCE api cannot create a shipment for an HVO order and vice versa.
    const ordersByShop = _.groupBy(ordersToExport, "shopSource");
    const created = [];
    // The order objects behind `created`, needed for the Trustpilot invitations: those may only
    // go to orders whose shipment really was created, never to skipped or failed ones.
    const createdOrders = [];
    const skipped = [];
    const failed = [];

    for (const [shopSource, shopOrders] of Object.entries(ordersByShop)) {
      // api/ answers with our Firestore document ids; staff think in order numbers.
      const orderNumberByDocId = shopOrders.reduce((map, order) => {
        map[order.id] = order.orderId;
        return map;
      }, {});
      const orderByDocId = shopOrders.reduce((map, order) => {
        map[order.id] = order;
        return map;
      }, {});

      try {
        const results = await createSooCoolShipments(
          shopSource,
          shopOrders.map((order) => order.id),
        );

        results.forEach((result) => {
          const orderNumber = orderNumberByDocId[result.orderId] || result.orderId;
          if (result.status === "created") {
            created.push(orderNumber);
            if (orderByDocId[result.orderId]) createdOrders.push(orderByDocId[result.orderId]);
          }
          // Keep the api's own reason with the order: "al naar SooCool gestuurd" and "al via
          // Trunkrs verstuurd (zending X)" are both skips, but they mean opposite things to the
          // person reading the notification.
          else if (result.status === "skipped") skipped.push({ orderId: orderNumber, reason: result.reason || null });
          else failed.push({ orderId: orderNumber, error: result.error || t("soocool.unknown_error") });
        });

        // An order the api did not answer for is not silently dropped.
        const answeredDocIds = results.map((result) => result.orderId);
        shopOrders
          .filter((order) => !answeredDocIds.includes(order.id))
          .forEach((order) => failed.push({ orderId: order.orderId, error: t("soocool.no_result") }));
      } catch (err) {
        // The whole batch for this shop failed (network, auth, api down): every order in it
        // is reported, so nobody assumes the shipments were created.
        const errorMessage = err.toString().replace("Error: ", "");
        shopOrders.forEach((order) => failed.push({ orderId: order.orderId, error: errorMessage }));
      }
    }

    setLoading(false);

    if (created.length > 0) {
      message.success(t("soocool.exported", { count: created.length }));
    }

    if (skipped.length > 0) {
      // One line per distinct reason instead of one sentence for the whole lot: an order held
      // back because Trunkrs already has it must not be reported as "al naar SooCool verstuurd",
      // which is the exact double booking the api-side guard exists to prevent.
      const skippedByReason = Object.entries(_.groupBy(skipped, (item) => item.reason || SKIP_REASON_MISSING));

      notification.warning({
        message: t("soocool.skipped_title", { count: skipped.length }),
        description: (
          <div>
            {skippedByReason.slice(0, 20).map(([reason, items]) => {
              const orders = formatOrderNumberList(items.map((item) => item.orderId));
              // An api that predates the `reason` field: fall back to the original wording.
              if (reason === SKIP_REASON_MISSING) {
                return <div key={reason}>{t("soocool.skipped_description", { orders })}</div>;
              }
              return (
                <div key={reason} style={{ marginBottom: 8 }}>
                  <div>{reason}</div>
                  <strong>{orders}</strong>
                </div>
              );
            })}
            {skippedByReason.length > 20 && <div>+ {skippedByReason.length - 20} meer</div>}
          </div>
        ),
        duration: 8,
      });
    }

    if (failed.length > 0) {
      notification.error({
        message: t("soocool.failed_title", { count: failed.length }),
        description: (
          <div>
            {failed.slice(0, 20).map((item) => (
              <div key={item.orderId}>
                <strong>{item.orderId}</strong>: {item.error}
              </div>
            ))}
            {failed.length > 20 && <div>+ {failed.length - 20} meer</div>}
          </div>
        ),
        duration: 0,
      });
    }

    if (created.length === 0 && skipped.length === 0 && failed.length === 0) {
      message.warning(t("soocool.no_orders"));
    }

    // Note: no "picked_up" mail here on purpose - whether SooCool sends its own track & trace
    // mail is still open (planning/soocool-koppeling.md, openstaand punt 7). Sending ours as
    // well would mail the customer twice.

    // The Trustpilot review invitation, on the other hand, DOES belong here. It used to hang off
    // the OptimoRoute export, which only ever saw VCE regional orders; now that every send order
    // of both shops leaves via SooCool, that button stops being used and this is the only place
    // left that still invites anyone. Same filters, same flag, same moment in the flow - only the
    // orders whose shipment was actually created.
    await sendTrustpilotReviewInvitations(createdOrders, updateOrder);

    // Refresh orders to pick up the soocool block and status written by api/
    await refreshOrders();
  };

  /**
   * Print the SooCool labels for the current scope: one PDF per shop, holding a label for every
   * colli of every SooCool order. Orders without a SooCool shipment keep the self-made A6 label
   * (the "Verzendlabel(s)" items above) - they are reported, not silently dropped.
   */
  const handleSooCoolLabelPrint = async () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const sooCoolOrders = getOrdersWithSooCoolShipment();
    const sendOrdersWithoutSooCool = ordersToFilter.filter(
      (order) => order.delivery?.type === DELIVERY_OPTIONS.SEND && !order.soocool?.orderId,
    );

    if (sooCoolOrders.length === 0) {
      notification.warning({
        message: t("soocool.no_labels_title"),
        description: t("soocool.no_labels_description"),
        duration: 8,
      });
      return;
    }

    setLoading(true);

    const downloadedShops = [];
    const failedShops = [];
    const ordersByShop = _.groupBy(sooCoolOrders, "shopSource");

    for (const [shopSource, shopOrders] of Object.entries(ordersByShop)) {
      try {
        const pdf = await fetchSooCoolLabelPdf(
          shopSource,
          shopOrders.map((order) => order.id),
          "a6",
        );
        downloadFile(pdf, `SooCool labels ${shopSource} ${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
        downloadedShops.push({ shopSource, count: shopOrders.length });
      } catch (err) {
        failedShops.push({ shopSource, error: err.toString().replace("Error: ", "") });
      }
    }

    setLoading(false);

    if (downloadedShops.length > 0) {
      message.success(
        t("soocool.labels_downloaded", {
          count: downloadedShops.reduce((total, shop) => total + shop.count, 0),
          files: downloadedShops.length,
        }),
      );
    }

    if (failedShops.length > 0) {
      notification.error({
        message: t("soocool.labels_failed_title"),
        description: (
          <div>
            {failedShops.map((item) => (
              <div key={item.shopSource}>
                <strong>{item.shopSource}</strong>: {item.error}
              </div>
            ))}
          </div>
        ),
        duration: 0,
      });
    }

    // The mixed case: a selection with both SooCool and non-SooCool orders. Say so, instead of
    // letting staff believe every parcel in the selection just got a label.
    if (sendOrdersWithoutSooCool.length > 0) {
      notification.info({
        message: t("soocool.mixed_selection_title", { count: sendOrdersWithoutSooCool.length }),
        description: t("soocool.mixed_selection_description", {
          orders: formatOrderNumberList(sendOrdersWithoutSooCool.map((order) => order.orderId)),
        }),
        duration: 0,
      });
    }
  };

  const getHvoDeliveryEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const hvoDeliveryOrders = ordersToFilter.filter(
      (order) => order.shopSource === "HVO" && order.delivery.type === DELIVERY_OPTIONS.SEND,
    );
    const uniqueEmails = [...new Set(hvoDeliveryOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({
      message: "HVO bezorging e-mails",
      description: emails || "Geen e-mails gevonden",
      duration: 0,
    });
  };

  const getVceLandelijkEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const vceLandelijkOrders = ordersToFilter.filter(
      (order) =>
        order.shopSource === "VCE" && order.delivery.type === DELIVERY_OPTIONS.SEND && order.landelijk === true,
    );
    const uniqueEmails = [...new Set(vceLandelijkOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({
      message: "VCE bezorging landelijk e-mails",
      description: emails || "Geen e-mails gevonden",
      duration: 0,
    });
  };

  const getVcePickupEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const vcePickupOrders = ordersToFilter.filter(
      (order) => order.shopSource === "VCE" && order.delivery.type === DELIVERY_OPTIONS.PICKUP,
    );
    const uniqueEmails = [...new Set(vcePickupOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({ message: "VCE afhaal e-mails", description: emails || "Geen e-mails gevonden", duration: 0 });
  };

  const getVceRegionaalEmails = () => {
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;
    const vceRegionaalOrders = ordersToFilter.filter(
      (order) =>
        order.shopSource === "VCE" && order.delivery.type === DELIVERY_OPTIONS.SEND && order.landelijk === false,
    );
    const uniqueEmails = [...new Set(vceRegionaalOrders.map((order) => order.customer.email))];
    const emails = uniqueEmails.join("; ");
    notification.open({
      message: "VCE bezorging regionaal e-mails",
      description: emails || "Geen e-mails gevonden",
      duration: 0,
    });
  };

  const getFilteredOrders = (orderStatusFilter, deliveryType) => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    // Filter by order status
    let filteredOrders =
      orderStatusFilter === ORDER_STATUS_PAID
        ? filterOrdersRows(ordersToFilter, ORDER_STATUS_PAID)
        : filterOrdersRows(ordersToFilter, null);

    // Return only shipment orders if deliveryType == send
    if (!!deliveryType && deliveryType === DELIVERY_OPTIONS.SEND) {
      _.remove(filteredOrders, (item) => item.delivery.type === DELIVERY_OPTIONS.PICKUP);
    }

    // Return only pickup orders if deliveryType == pickup
    if (!!deliveryType && deliveryType === DELIVERY_OPTIONS.PICKUP) {
      _.remove(filteredOrders, (item) => item.delivery.type === DELIVERY_OPTIONS.SEND);
    }

    // Order by date ascending
    return _.orderBy(filteredOrders, ["createdAt"], ["asc"]);
  };

  const getLandelijkOrders = () => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    // Filter for national orders: VCE landelijk + HVO with delivery.type === "send"
    const filteredOrders = ordersToFilter.filter(
      (order) =>
        order.delivery?.type === DELIVERY_OPTIONS.SEND &&
        ((order.shopSource === "VCE" && order.landelijk === true) || order.shopSource === "HVO"),
    );

    // Order by date ascending
    return _.orderBy(filteredOrders, ["createdAt"], ["asc"]);
  };

  const getRegionaalOrders = () => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    // Filter for regional orders: VCE regionaal (both SEND and PICKUP)
    const filteredOrders = ordersToFilter.filter(
      (order) =>
        order.shopSource === "VCE" &&
        (order.landelijk === false || order.delivery?.type === DELIVERY_OPTIONS.PICKUP) &&
        (order.delivery?.type === DELIVERY_OPTIONS.SEND || order.delivery?.type === DELIVERY_OPTIONS.PICKUP),
    );

    // Verzenders eerst, dan afhalers; binnen elke groep op aanmaakdatum
    return _.orderBy(
      filteredOrders,
      [(order) => (order.delivery?.type === DELIVERY_OPTIONS.PICKUP ? 1 : 0), "createdAt"],
      ["asc", "asc"],
    );
  };

  const getVceLandelijkOrders = () => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    // Filter for VCE-only national orders with delivery.type === "send"
    const filteredOrders = ordersToFilter.filter(
      (order) =>
        order.shopSource === "VCE" && order.delivery?.type === DELIVERY_OPTIONS.SEND && order.landelijk === true,
    );

    // Order by date ascending
    return _.orderBy(filteredOrders, ["createdAt"], ["asc"]);
  };

  const getHvoOrders = () => {
    // Use selected rows if any, otherwise use all orders for selected date
    const ordersToFilter = selectedRows.length > 0 ? selectedRows : orders;

    // Filter for HVO-only orders with delivery.type === "send"
    const filteredOrders = ordersToFilter.filter(
      (order) => order.shopSource === "HVO" && order.delivery?.type === DELIVERY_OPTIONS.SEND,
    );

    // Order by date ascending
    return _.orderBy(filteredOrders, ["createdAt"], ["asc"]);
  };

  // No longer needed - filterOrdersDateRange removed

  const filterOrdersRows = (orders, statusFilter) => {
    return !!statusFilter
      ? _.filter(orders, function (order) {
          return order.orderStatus === statusFilter;
        })
      : orders;
  };

  const CombinedPackingSend = React.forwardRef((props, ref) => {
    const { data } = props;

    return (
      <div ref={ref}>
        {data.map((order, index) => (
          <div key={order.id}>
            <PackingSlip pickRouteIndex={pickRouteIndex} data={enrichOrdersWithProductData([order], products)} />
            <div style={{ pageBreakAfter: "always" }}></div>
            <SendLabel data={getSendListData([order])} />
            {index < data.length - 1 && <div style={{ pageBreakAfter: "always" }}></div>}
          </div>
        ))}
      </div>
    );
  });

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => openEditModal(row)}>
          <Flex alignItems="center">
            <EditOutlined />
            <span style={{ marginLeft: 4 }}>{t("edit")}</span>
          </Flex>
        </a>
      </Menu.Item>
      <Menu.Item>
        <a onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </a>
      </Menu.Item>
    </Menu>
  );

  const exportMenu = (
    <Menu>
      <Menu.Item key="1" onClick={handleOptimoRouteExport}>
        <CarOutlined /> <span>{t("optimoroute")} (VCE Regionaal)</span>
      </Menu.Item>
      {/* Regional now also goes out via SooCool, so the two regional buttons sit next to each
          other; `getCarrierBooking` stops an order being handed to both. */}
      {/* Trunkrs only carries Monday since SooCool (see `handleTrunkrsExport`). */}
      <Menu.Item key="2" onClick={handleTrunkrsExport} disabled={selectedDate.day() === 6}>
        <CarOutlined /> <span>Trunkrs (maandag)</span>
      </Menu.Item>
      <Menu.Item key="3" onClick={handleChillBillExport}>
        <FileExcelOutlined /> <span>Chill Bill (Landelijk)</span>
      </Menu.Item>
      <Menu.Item key="4" onClick={handleSooCoolExport}>
        <CarOutlined /> <span>{t("soocool.export_menu")}</span>
      </Menu.Item>
    </Menu>
  );

  const printMenu = (
    <Menu>
      <Menu.Item key="2" onClick={() => handleManualPrint(handlePickListPrint)}>
        {t("order.pick_list")}
      </Menu.Item>
      <Menu.SubMenu key="packing-slips" title={t("order.packing_slips")} icon={<PrinterOutlined />}>
        <Menu.Item key="packing-slips-landelijk" onClick={() => handleManualPrint(handlePackingSlipPrintLandelijk)}>
          Landelijk (VCE + HVO)
        </Menu.Item>
        <Menu.Item
          key="packing-slips-landelijk-vce"
          onClick={() => handleManualPrint(handlePackingSlipPrintVceLandelijk)}
        >
          Landelijk VCE
        </Menu.Item>
        <Menu.Item
          key="packing-slips-landelijk-hvo"
          onClick={() => handleManualPrint(handlePackingSlipPrintHvoLandelijk)}
        >
          Landelijk HVO
        </Menu.Item>
        <Menu.Item key="packing-slips-regionaal" onClick={() => handleManualPrint(handlePackingSlipPrintRegionaal)}>
          Regionaal (VCE)
        </Menu.Item>
        <Menu.Item key="packing-slips-all" onClick={() => handleManualPrint(handlePackingSlipPrint)}>
          Alle
        </Menu.Item>
      </Menu.SubMenu>
      <Menu.SubMenu key="send-labels" title={t("order.send_labels")} icon={<PrinterOutlined />}>
        {/* SooCool labels come as a PDF from api/ (one per colli), the rest is the self-made A6 */}
        <Menu.Item key="send-labels-soocool" onClick={handleSooCoolLabelPrint}>
          {t("soocool.labels_menu")}
        </Menu.Item>
        <Menu.Item key="send-labels-landelijk" onClick={() => handleManualPrint(handleSendListPrintLandelijk)}>
          Landelijk (VCE + HVO)
        </Menu.Item>
        <Menu.Item key="send-labels-landelijk-vce" onClick={() => handleManualPrint(handleSendListPrintVceLandelijk)}>
          Landelijk VCE
        </Menu.Item>
        <Menu.Item key="send-labels-landelijk-hvo" onClick={() => handleManualPrint(handleSendListPrintHvoLandelijk)}>
          Landelijk HVO
        </Menu.Item>
        <Menu.Item key="send-labels-regionaal" onClick={() => handleManualPrint(handleSendListPrintRegionaal)}>
          Regionaal (VCE)
        </Menu.Item>
        <Menu.Item key="send-labels-all" onClick={() => handleManualPrint(handleSendListPrint)}>
          Alle
        </Menu.Item>
      </Menu.SubMenu>
      <Menu.SubMenu key="packing-send" title={t("order.packing_slips_and_send_labels")} icon={<PrinterOutlined />}>
        <Menu.Item
          key="packing-send-landelijk"
          onClick={() => handleManualPrint(handleCombinedPackingSendPrintLandelijk)}
        >
          Landelijk (VCE + HVO)
        </Menu.Item>
        <Menu.Item
          key="packing-send-landelijk-vce"
          onClick={() => handleManualPrint(handleCombinedPackingSendPrintVceLandelijk)}
        >
          Landelijk VCE
        </Menu.Item>
        <Menu.Item
          key="packing-send-landelijk-hvo"
          onClick={() => handleManualPrint(handleCombinedPackingSendPrintHvoLandelijk)}
        >
          Landelijk HVO
        </Menu.Item>
        <Menu.Item
          key="packing-send-regionaal"
          onClick={() => handleManualPrint(handleCombinedPackingSendPrintRegionaal)}
        >
          Regionaal (VCE)
        </Menu.Item>
        <Menu.Item key="packing-send-all" onClick={() => handleManualPrint(handleCombinedPackingSendPrint)}>
          Alle
        </Menu.Item>
      </Menu.SubMenu>
      <Menu.SubMenu key="emails" title={<span>E-mailadressen</span>} icon={<MailOutlined />}>
        <Menu.Item key="email-hvo" onClick={getHvoDeliveryEmails}>
          HVO Bezorging
        </Menu.Item>
        <Menu.Item key="email-vce-landelijk" onClick={getVceLandelijkEmails}>
          VCE Bezorging Landelijk
        </Menu.Item>
        <Menu.Item key="email-vce-pickup" onClick={getVcePickupEmails}>
          VCE Afhaal
        </Menu.Item>
        <Menu.Item key="email-vce-regionaal" onClick={getVceRegionaalEmails}>
          VCE Bezorging Regionaal
        </Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  const tableColumns = [
    {
      title: "Shop",
      dataIndex: "shopSource",
      render: (shopSource, record) => <Tag color={getShopBadgeColor(record)}>{shopSource}</Tag>,
      sorter: (a, b) => a.shopSource.localeCompare(b.shopSource),
      width: 80,
    },
    {
      title: t("order.order_id"),
      dataIndex: "orderId",
    },
    {
      title: t("email"),
      dataIndex: "customer",
      render: (data) => <span>{data.email}</span>,
    },
    {
      title: t("user"),
      dataIndex: "customer",
      render: (data) => (
        <span>
          {data.firstName} {data.lastName}
        </span>
      ),
    },
    {
      title: t("order_date"),
      dataIndex: "createdAt",
      render: (data) => <span>{dayjs.unix(data).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "createdAt"),
      defaultSortOrder: "descend",
    },
    {
      title: t("pickup_delivery_date"),
      dataIndex: "delivery",
      render: (data) => (
        <span>{!!data && !!data.date ? dayjs(data.date, DATE_FORMAT_DD_MM_YYYY).format("ddd D MMMM YYYY") : "-"}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.delivery.date, DATE_FORMAT_DD_MM_YYYY).unix() - dayjs(b.delivery.date, DATE_FORMAT_DD_MM_YYYY).unix(),
    },
    {
      title: t("pickup_type"),
      dataIndex: "delivery",
      render: (data) => (
        <span>{!!data ? (data.type === DELIVERY_OPTIONS.SEND ? t("shipping") : t("pickup_short")) : "-"}</span>
      ),
    },
    {
      title: t("order.carrier"),
      dataIndex: "soocool",
      width: 130,
      render: (soocool, record) => {
        if (!!soocool?.orderId) {
          const colliCount = soocool.goods?.length || 0;
          const trackAndTraceLink = getSooCoolTrackAndTraceLink(soocool);
          const tag = <Tag color="volcano">SooCool{colliCount > 1 ? ` (${colliCount}x)` : ""}</Tag>;
          if (!trackAndTraceLink) return tag;
          return (
            <a
              href={trackAndTraceLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={t("soocool.track_and_trace")}
            >
              {tag}
            </a>
          );
        }
        if (!!record.trunkrsNumber) return <Tag color="blue">Trunkrs</Tag>;
        // Regional orders can go out via either carrier now, so the row has to say which one.
        if (!!record.optimoRouteExportedAt) return <Tag color="green">OptimoRoute</Tag>;
        // Exported before OptimoRoute wrote a marker. Shown - greyed out, with the hint - because
        // the SooCool export holds this order back as already booked, and a row reading "-" while
        // that happens looks like a bug instead of an old order.
        if (isLegacyOptimoRouteBooking(record)) {
          return <Tag title={t("carrier.unmarked_hint")}>OptimoRoute ({t("carrier.unmarked_tag")})</Tag>;
        }
        return <span>-</span>;
      },
    },
    {
      title: t("status"),
      dataIndex: "orderStatus",
      render: (_, order) => (
        <Tag color={getPaymentStatusColor(order.orderStatus)}>{getPaymentStatus(order.orderStatus)}</Tag>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "orderStatus"),
    },
    {
      title: t("total"),
      dataIndex: "totalPrice",
      render: (data) => <span className="font-weight-semibold">{CURRENCY + formatPrice(data)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPrice"),
    },
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const orderDetailsColumns = [
    {
      title: t("name"),
      render: (_, data) => (
        <div className="d-flex">
          <span>
            {data.name} {data.type && "(" + data.type + ")"}
          </span>
        </div>
      ),
    },
    {
      title: t("price"),
      dataIndex: "originalPrice",
      render: (data) => <span>€{formatPrice(data)}</span>,
      align: "center",
    },
    {
      title: t("weight"),
      dataIndex: "variation",
      render: (data) => (data && data.weight ? utils.formatWeight(data.weight) + t("gram_unit") : "-"),
      align: "center",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      render: (data) => <span>{data}</span>,
      align: "center",
    },
    {
      title: t("discount"),
      dataIndex: "discountPercentage",
      render: (data) => <span>{data}%</span>,
      align: "center",
    },
    {
      title: t("sub_total"),
      dataIndex: "paymentPrice",
      render: (_, data) => <span>€{formatPrice(data.paymentPrice * data.quantity)}</span>,
      align: "right",
    },
  ];

  /**
   * One row per box of a SooCool shipment. Reads the write-back shape stored on our own order
   * (`weightGram`), but goes through the shared helpers so the SooCool page can show the raw API
   * shape (`weight`) with the same rendering.
   */
  const soocoolColliColumns = [
    {
      title: "#",
      width: 50,
      align: "center",
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: t("soocool.barcode"),
      dataIndex: "barcode",
      render: (barcode) => <span style={{ fontFamily: "monospace" }}>{barcode || "-"}</span>,
    },
    {
      title: t("weight"),
      align: "right",
      render: (_, good) => <span>{formatSooCoolGoodWeight(good) || "-"}</span>,
    },
    {
      title: t("soocool.contents"),
      dataIndex: "contents",
      render: (contents) => <span>{contents || "-"}</span>,
    },
    {
      title: t("soocool.transport_requirements"),
      dataIndex: "transportRequirements",
      render: (requirements) => {
        const list = Array.isArray(requirements) ? requirements : [];
        if (list.length === 0) return <span>-</span>;
        return list.map((requirement) => (
          <Tag key={requirement} color={getSooCoolTransportRequirementColor(requirement)}>
            {t(`soocool.transport_requirement.${requirement}`, { defaultValue: requirement })}
          </Tag>
        ));
      },
    },
  ];

  /**
   * The SooCool block of the order details drawer: what we stored on the order when the shipment
   * was created (planning/soocool-koppeling.md, "Write-back op de order"). An order that never
   * went via SooCool renders nothing at all, so the drawer is unchanged for it.
   */
  const renderSooCoolShipment = (soocool) => {
    const deliveryTask = getSooCoolDeliveryTask(soocool);
    const trackAndTraceLink = getSooCoolTrackAndTraceLink(soocool);
    const plannedTimeWindow = formatSooCoolTimeWindow(getSooCoolPlannedTimeWindow(deliveryTask));
    const completionProblem = getSooCoolCompletionProblem(deliveryTask);
    const deliveredAtNeighbours = getSooCoolDeliveredAtNeighbours(deliveryTask);
    const neighbourHouseNumber = getSooCoolNeighbourHouseNumber(deliveryTask);
    const trackAndTraceMail = getSooCoolTrackAndTraceMail(soocool, deliveryTask);
    const goods = soocool.goods || [];
    // A redelivery leaves the failed attempt in the list forever - it carries the completionProblem,
    // which is exactly what staff need when a customer calls. The block above shows the CURRENT
    // attempt (see getSooCoolDeliveryTask); these are the ones before it.
    const earlierAttempts = (soocool.tasks || []).filter(
      (task) => task?.taskType === "delivery" && task !== deliveryTask,
    );

    return (
      <div className="pt-5">
        <Descriptions
          bordered
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{t("soocool.drawer_title")}</span>
              <Tag color="volcano">{t("soocool.colli_count", { count: goods.length })}</Tag>
            </div>
          }
          size="small"
          column={1}
        >
          <Descriptions.Item label={t("soocool.order_id")}>{soocool.orderId}</Descriptions.Item>
          <Descriptions.Item label={t("soocool.our_reference")}>{soocool.ourReference || "-"}</Descriptions.Item>
          <Descriptions.Item label={t("soocool.order_reference")}>{soocool.orderReference || "-"}</Descriptions.Item>
          <Descriptions.Item label={t("soocool.task_state_label")}>
            {deliveryTask?.taskState ? (
              <Tag color={getSooCoolTaskStateColor(deliveryTask.taskState)}>
                {t(`soocool.task_state.${deliveryTask.taskState}`, { defaultValue: deliveryTask.taskState })}
              </Tag>
            ) : (
              <span>-</span>
            )}
          </Descriptions.Item>
          {!!plannedTimeWindow && (
            <Descriptions.Item label={t("soocool.planned_time_window")}>{plannedTimeWindow}</Descriptions.Item>
          )}
          {!!completionProblem && (
            <Descriptions.Item label={t("soocool.completion_problem")}>
              {t(`soocool.completion_problem_value.${completionProblem}`, { defaultValue: completionProblem })}
            </Descriptions.Item>
          )}
          {deliveredAtNeighbours && (
            <Descriptions.Item label={t("soocool.delivered_at_neighbours")}>
              {neighbourHouseNumber
                ? t("soocool.delivered_at_neighbours_house_number", { houseNumber: neighbourHouseNumber })
                : t("yes")}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t("soocool.track_and_trace_label")}>
            {trackAndTraceLink ? (
              <a href={trackAndTraceLink} target="_blank" rel="noopener noreferrer">
                {t("soocool.track_and_trace")}
              </a>
            ) : (
              <span style={{ color: "#8c8c8c" }}>{t("soocool.no_track_and_trace")}</span>
            )}
          </Descriptions.Item>
          {/* Our own track & trace mail to the customer, sent once SooCool has planned this attempt */}
          <Descriptions.Item label={t("soocool.track_and_trace_mail")}>
            {trackAndTraceMail ? (
              t("soocool.track_and_trace_mail_sent", {
                date: trackAndTraceMail.mailedAt
                  ? dayjs(Number(trackAndTraceMail.mailedAt)).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)
                  : "-",
                email: trackAndTraceMail.email || "-",
              })
            ) : (
              <span style={{ color: "#8c8c8c" }}>{t("soocool.track_and_trace_mail_pending")}</span>
            )}
          </Descriptions.Item>
        </Descriptions>
        {earlierAttempts.length > 0 && (
          <div className="pt-3">
            <h4>{t("soocool.earlier_attempts", { count: earlierAttempts.length })}</h4>
            <Descriptions bordered size="small" column={1}>
              {earlierAttempts.map((task, index) => {
                const problem = getSooCoolCompletionProblem(task);
                return (
                  <Descriptions.Item
                    key={task?.taskId ?? index}
                    label={t("soocool.attempt_label", { number: index + 1 })}
                  >
                    <Tag color={getSooCoolTaskStateColor(task?.taskState)}>
                      {t(`soocool.task_state.${task?.taskState}`, { defaultValue: task?.taskState || "-" })}
                    </Tag>
                    {!!problem && (
                      <span style={{ marginLeft: 8 }}>
                        {t(`soocool.completion_problem_value.${problem}`, { defaultValue: problem })}
                      </span>
                    )}
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          </div>
        )}
        <div className="table-responsive pt-3">
          <h4>{t("soocool.colli_heading", { count: goods.length })}</h4>
          <Table
            showSorterTooltip={false}
            columns={soocoolColliColumns}
            dataSource={goods}
            rowKey={(good, index) => good?.goodId ?? good?.barcode ?? index}
            pagination={false}
            size="small"
            bordered={true}
            className="w-100"
          />
        </div>
      </div>
    );
  };

  const rowSelection = {
    onChange: (key, rows) => {
      setSelectedRows(rows);
      setSelectedRowKeys(key);
    },
  };

  const onSearch = (e) => {
    const value = e.currentTarget.value;
    setSearchText(value);
    setSelectedRowKeys([]);
  };

  const findOrderById = async (value) => {
    const input = (value || orderIdSearch || "").trim();
    if (!input) {
      message.warning(t("enter_order_id"));
      return;
    }
    try {
      setFindingById(true);
      const foundOrder = await findOrderByIdHook(input);
      if (foundOrder) {
        showDrawer(foundOrder);
      } else {
        message.error(t("order_not_found", { orderId: input }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to find order by ID", err);
      message.error(t("order_not_found", { orderId: input }));
    } finally {
      setFindingById(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date || dayjs());
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const openEditModal = (order) => {
    setOrderToEdit(order);
    setEditOrderModalVisible(true);
  };

  const closeEditModal = () => {
    setEditOrderModalVisible(false);
    setOrderToEdit(null);
  };

  const downloadInvoice = (order) => {
    let invoiceOrder = {
      ...order,
      language: "nl",
      orderDate: formatOrderDate(order.createdAt),
    };
    generateInvoice(invoiceOrder).then((response) => {
      response.blob().then((blob) => downloadFile(blob, formatInvoiceFileName(t("invoice"), order.orderId)));
    });
  };

  /**
   * Export statistics, attributed per carrier.
   *
   * Each delivery group carries a `carriers` map: how many of its orders went out with which
   * carrier, read off the marker on the order itself (`trunkrsNumber` / `soocool.orderId`) via
   * `getCarrierBooking`. That replaces the old single `exported` counter, which was rendered
   * under a hardcoded "-> Trunkrs" label and made one SooCool order show up twice: once as a
   * Trunkrs export and once in a separate SooCool row.
   *
   * ChillBill writes no marker at all (see the note at `handleChillBillExport`), so nothing can
   * honestly be counted for it. Those orders stay in the "not exported yet" remainder rather than
   * being faked into a carrier count.
   */
  const exportStats = useMemo(() => {
    const emptyGroup = () => ({ total: 0, exported: 0, carriers: {}, soocoolColli: 0, unmarked: 0 });

    const stats = {
      total: 0,
      vceRegional: emptyGroup(),
      vcePickup: { total: 0, winkel: 0, schepenwijk: 0 },
      vceNational: emptyGroup(),
      hvoDelivery: emptyGroup(),
    };

    const countCarrier = (group, carrier, order) => {
      group.carriers[carrier] = (group.carriers[carrier] || 0) + 1;
      group.exported++;
      // A SooCool shipment is one order but potentially several boxes; the colli total is what
      // tells the packing table how many labels belong to this group.
      if (carrier === CARRIER_SOOCOOL) group.soocoolColli += order.soocool?.goods?.length || 1;
    };

    orders.forEach((order) => {
      const isShipped = order.orderStatus === ORDER_STATUS_SHIPPED;
      const isPaid = order.orderStatus !== ORDER_STATUS_CREATED;
      const bookedCarrier = getCarrierBooking(order);

      // Only count PAID or SHIPPED orders
      if (isPaid || isShipped) {
        stats.total++;

        // VCE orders
        if (order.shopSource === "VCE") {
          // Pickup orders
          if (order.delivery?.type === DELIVERY_OPTIONS.PICKUP) {
            stats.vcePickup.total++;
            const location = order.delivery?.location?.toLowerCase();
            if (location === "winkel") {
              stats.vcePickup.winkel++;
            } else if (location === "schepenwijk") {
              stats.vcePickup.schepenwijk++;
            }
          }
          // Delivery orders
          else if (order.delivery?.type === DELIVERY_OPTIONS.SEND) {
            // Regional delivery -> SooCool or OptimoRoute. The marker on the order decides, and
            // it has to win over the status: OptimoRoute and SooCool both write SHIPPED, so
            // inferring from SHIPPED alone would book every regional SooCool order to OptimoRoute.
            //
            // Orders exported before OptimoRoute started writing `optimoRouteExportedAt` are
            // attributed to it by `isLegacyOptimoRouteBooking`, so history does not go blank.
            // They are counted separately as well: for those the carrier is inferred from the
            // status instead of recorded on the order, which is what the note under the row says.
            if (order.landelijk === false) {
              stats.vceRegional.total++;
              if (bookedCarrier) {
                countCarrier(stats.vceRegional, bookedCarrier, order);
                if (isLegacyOptimoRouteBooking(order)) stats.vceRegional.unmarked++;
              }
            }
            // National delivery -> Trunkrs / Chill Bill / SooCool
            else if (order.landelijk === true) {
              stats.vceNational.total++;
              if (bookedCarrier) countCarrier(stats.vceNational, bookedCarrier, order);
            }
          }
        }
        // HVO orders (all delivery -> Trunkrs / Chill Bill / SooCool)
        else if (order.shopSource === "HVO" && order.delivery?.type === DELIVERY_OPTIONS.SEND) {
          stats.hvoDelivery.total++;
          if (bookedCarrier) countCarrier(stats.hvoDelivery, bookedCarrier, order);
        }
      }
    });

    return stats;
  }, [orders]);

  /**
   * The ".. 12 / 12 -> 8x Trunkrs, 4x SooCool (7 colli)" tail of a stats row.
   *
   * Only carriers that actually carried something are named, so no order can appear under a
   * carrier it did not go with. Whatever is left is reported as a remainder, never silently
   * folded into a carrier.
   */
  const renderExportBreakdown = (group, accentColor, remainderNote) => {
    const usedCarriers = CARRIER_ORDER.filter((carrier) => (group.carriers[carrier] || 0) > 0);
    const remainder = group.total - group.exported;

    return (
      <>
        <span style={{ color: "#bfbfbf" }}>•</span>
        <span style={{ color: accentColor, fontWeight: 600 }}>{group.exported}</span>
        <span style={{ color: "#8c8c8c" }}>/ {group.total}</span>
        {usedCarriers.length > 0 && (
          <span style={{ color: "#8c8c8c", fontSize: "12px" }}>
            →{" "}
            {usedCarriers
              .map((carrier) => {
                const count = group.carriers[carrier];
                if (carrier === CARRIER_SOOCOOL) {
                  return `${count}× ${CARRIER_LABELS[carrier]} (${group.soocoolColli} colli)`;
                }
                return `${count}× ${CARRIER_LABELS[carrier]}`;
              })
              .join(", ")}
          </span>
        )}
        {remainder > 0 && (
          <span style={{ color: "#8c8c8c", fontSize: "12px" }}>
            {usedCarriers.length > 0 && <span style={{ color: "#bfbfbf", marginRight: "6px" }}>•</span>}
            {remainder} {remainderNote}
          </span>
        )}
        {group.unmarked > 0 && (
          <span style={{ color: "#fa8c16", fontSize: "12px" }} title={t("carrier.unmarked_hint")}>
            <span style={{ color: "#bfbfbf", marginRight: "6px" }}>•</span>
            {t("carrier.unmarked_note", { count: group.unmarked })}
          </span>
        )}
      </>
    );
  };

  // Saturday is an ordinary SooCool delivery day (planning/soocool-koppeling.md, besluit 1), so a
  // remainder means the same there as on any other day: these orders still have to go out via
  // SooCool. The wording used to point at the Chill Bill export instead, which is the one export
  // that leaves NO marker on the order (see the note at `handleChillBillExport`) - running it on
  // top of SooCool gives a double booking this screen can never show. That button stays available
  // during the transition, but nothing in this bar should send anyone to it.
  const nationalRemainderNote = "nog niet geëxporteerd";

  return (
    <>
      <Card>
        {/* Date indicator banner */}
        <div
          style={{
            backgroundColor: "#f0f2f5",
            padding: "12px 16px",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <span style={{ fontWeight: 500, fontSize: "14px" }}>Aflever-/ophaaldatum:</span>
            <span style={{ fontSize: "16px", fontWeight: 600, color: "#1890ff" }}>
              {selectedDate.format("dddd D MMMM YYYY")}
              {selectedDate.day() === 5 && (
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#8c8c8c", marginLeft: "8px" }}>
                  (incl. zaterdag {selectedDate.add(1, "day").format("D MMMM")})
                </span>
              )}
            </span>
          </div>
          {/* Order statistics overview */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* Total orders */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
              <span style={{ fontWeight: 600, minWidth: "140px" }}>Totaal bestellingen:</span>
              <span style={{ fontSize: "15px", fontWeight: 700, color: "#262626" }}>{exportStats.total}</span>
            </div>

            {/* VCE Regional Delivery */}
            {exportStats.vceRegional.total > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                <Tag color="green" style={{ margin: 0, minWidth: "140px" }}>
                  VCE Regionaal
                </Tag>
                <span style={{ fontWeight: 600 }}>{exportStats.vceRegional.total}</span>
                <span style={{ color: "#8c8c8c" }}>bestellingen</span>
                {renderExportBreakdown(exportStats.vceRegional, "#52c41a", "nog niet geëxporteerd")}
              </div>
            )}

            {/* VCE Pickup */}
            {exportStats.vcePickup.total > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                <Tag color="orange" style={{ margin: 0, minWidth: "140px" }}>
                  VCE Afhalen
                </Tag>
                <span style={{ fontWeight: 600 }}>{exportStats.vcePickup.total}</span>
                <span style={{ color: "#8c8c8c" }}>bestellingen</span>
                {(exportStats.vcePickup.winkel > 0 || exportStats.vcePickup.schepenwijk > 0) && (
                  <>
                    <span style={{ color: "#bfbfbf" }}>•</span>
                    {exportStats.vcePickup.winkel > 0 && (
                      <>
                        <span style={{ color: "#fa8c16", fontWeight: 600 }}>{exportStats.vcePickup.winkel}</span>
                        <span style={{ color: "#8c8c8c" }}>winkel</span>
                      </>
                    )}
                    {exportStats.vcePickup.winkel > 0 && exportStats.vcePickup.schepenwijk > 0 && (
                      <span style={{ color: "#bfbfbf" }}>,</span>
                    )}
                    {exportStats.vcePickup.schepenwijk > 0 && (
                      <>
                        <span style={{ color: "#fa8c16", fontWeight: 600 }}>{exportStats.vcePickup.schepenwijk}</span>
                        <span style={{ color: "#8c8c8c" }}>schepenwijk</span>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* VCE National Delivery */}
            {exportStats.vceNational.total > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                <Tag color="blue" style={{ margin: 0, minWidth: "140px" }}>
                  VCE Landelijk
                </Tag>
                <span style={{ fontWeight: 600 }}>{exportStats.vceNational.total}</span>
                <span style={{ color: "#8c8c8c" }}>bestellingen</span>
                {renderExportBreakdown(exportStats.vceNational, "#1890ff", nationalRemainderNote)}
              </div>
            )}

            {/* HVO Delivery */}
            {exportStats.hvoDelivery.total > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                <Tag color="purple" style={{ margin: 0, minWidth: "140px" }}>
                  HVO Bezorging
                </Tag>
                <span style={{ fontWeight: 600 }}>{exportStats.hvoDelivery.total}</span>
                <span style={{ color: "#8c8c8c" }}>bestellingen</span>
                {renderExportBreakdown(exportStats.hvoDelivery, "#722ed1", nationalRemainderNote)}
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Input
              value={searchText}
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e)}
              style={{ width: "fit-content" }}
            />
            <Input.Search
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              placeholder={t("search_order_by_id")}
              enterButton={t("search")}
              loading={findingById}
              onSearch={findOrderById}
              style={{ width: "fit-content" }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Tooltip title={t("pickup_delivery_date")}>
              <Form form={datePickerForm}>
                <Form.Item name="deliveryDate" style={{ marginBottom: 0 }} initialValue={dayjs()}>
                  <DatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    locale={locale}
                    placeholder={t("delivery_date")}
                    format={DATE_FORMAT_DD_MM_YYYY}
                    allowClear={false}
                  />
                </Form.Item>
              </Form>
            </Tooltip>
            <Dropdown overlay={printMenu}>
              <Button className="ant-btn-primary">
                <PrinterOutlined /> {t("print")} <DownOutlined />
              </Button>
            </Dropdown>
            <Dropdown overlay={exportMenu}>
              <Button className="ant-btn-primary">
                <FileExcelOutlined /> {t("export")} <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <div className="table-responsive">
          <Table
            showSorterTooltip={false}
            columns={tableColumns}
            dataSource={filteredList}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: selectedRowKeys,
              type: "checkbox",
              preserveSelectedRowKeys: false,
              ...rowSelection,
            }}
            scroll={{ x: 1430 }}
            loading={dataLoading}
            onRow={(record, t) => {
              return {
                onClick: (e) => {
                  if (e.target.className.includes("ant-tag") || e.target.tagName === "A") return;
                  showDrawer(record);
                },
              };
            }}
          />
        </div>
        {!!order && (
          <Drawer
            title={`${order.orderId} - ${order.shopName}`}
            placement="right"
            onClose={closeDrawer}
            visible={visible}
            width={isMobile ? window.innerWidth : 720}
          >
            <Descriptions
              bordered
              title={
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{t("order.order")}</span>
                  <Tag color={getPaymentStatusColor(order.orderStatus)}>{getPaymentStatus(order.orderStatus)}</Tag>
                </div>
              }
              size="small"
              column={1}
            >
              <Descriptions.Item label={t("order_date")}>
                {dayjs.unix(order.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
              </Descriptions.Item>
              <Descriptions.Item label={t("name")}>
                {order.customer.firstName} {order.customer.lastName}
              </Descriptions.Item>
              <Descriptions.Item label={t("email")}>{order.customer.email}</Descriptions.Item>
              <Descriptions.Item label={t("phone")}>{order.customer.phoneNumber}</Descriptions.Item>
              <Descriptions.Item label={t("delivery_address")}>
                {order.customer.address.delivery.firstName} {order.customer.address.delivery.lastName} <br />
                {order.customer.address.delivery.companyName} {order.customer.address.delivery.companyName && <br />}
                {order.customer.address.delivery.street} {order.customer.address.delivery.houseNumber}{" "}
                {order.customer.address.delivery.addition}
                <br />
                {order.customer.address.delivery.zipCode}, {order.customer.address.delivery.city}
              </Descriptions.Item>
              {!!order.customer.address.invoice && (
                <Descriptions.Item label={t("invoice_address")}>
                  {order.customer.address.invoice.firstName} {order.customer.address.invoice.lastName} <br />
                  {order.customer.address.invoice.companyName} {order.customer.address.invoice.companyName && <br />}
                  {order.customer.address.invoice.street} {order.customer.address.invoice.houseNumber}{" "}
                  {order.customer.address.invoice.addition}
                  <br />
                  {order.customer.address.invoice.zipCode}, {order.customer.address.invoice.city}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("shipping_method")}>
                {formatShippingMethod(order.delivery.type)}
              </Descriptions.Item>
              {order.delivery.type === DELIVERY_OPTIONS.PICKUP && (
                <Descriptions.Item label={t("pickup_location")}>
                  {order.delivery?.pickupLocation === "schepenwijk"
                    ? "Schepenwijk (De Hoek 91A)"
                    : "Winkel (Melkmarkt 10)"}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("pickup_delivery_date")}>
                {dayjs(order.delivery.date, DATE_FORMAT_DD_MM_YYYY).format("ddd D MMMM YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label={t("order_notes")}>
                {!!order.orderNotes ? order.orderNotes : "-"}
              </Descriptions.Item>
            </Descriptions>
            {!!order.soocool?.orderId && renderSooCoolShipment(order.soocool)}
            <div className="table-responsive pt-5">
              <h4>{t("products")}</h4>
              <Table
                showSorterTooltip={false}
                columns={
                  hasWeightProperty
                    ? orderDetailsColumns
                    : orderDetailsColumns.filter((col) => col.dataIndex !== "variation")
                }
                dataSource={order.products}
                pagination={false}
                rowKey="variantId"
                className="w-100"
                bordered={true}
                summary={(pageData) => {
                  let totalAmount = 0;
                  let totalDiscount = 0;

                  pageData.forEach(({ quantity, discount }) => {
                    totalAmount += quantity;
                    totalDiscount += discount;
                  });
                  return (
                    !!order && (
                      <>
                        {order.delivery.type === DELIVERY_OPTIONS.SEND && (
                          <Table.Summary.Row>
                            <Table.Summary.Cell>
                              <Text>{t("shipping")}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            {hasWeightProperty && <Table.Summary.Cell />}
                            <Table.Summary.Cell align="right">
                              <Text>{!!order.delivery.cost ? "€" + formatPrice(order.delivery.cost) : "-"}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                        {!!order.discount && (
                          <Table.Summary.Row>
                            <Table.Summary.Cell>
                              <Text>
                                {!!order.discount.percentage ? (
                                  <Trans
                                    i18nKey="cart_discount_percentage"
                                    values={{
                                      discountPercentage: order.discount.percentage,
                                      discountCode: order.discount.code,
                                    }}
                                  />
                                ) : (
                                  <Trans
                                    i18nKey="cart_discount_amount"
                                    values={{
                                      discountAmount: order.discount.amount,
                                      discountCode: order.discount.code,
                                    }}
                                  />
                                )}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            <Table.Summary.Cell />
                            {hasWeightProperty && <Table.Summary.Cell />}
                            <Table.Summary.Cell align="right">
                              <Text>- {CURRENCY + formatPrice(order.discount.totalDiscount)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                        <Table.Summary.Row>
                          <Table.Summary.Cell>
                            <Text strong={true}>{t("total")}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="center" />
                          {hasWeightProperty && <Table.Summary.Cell align="center" />}
                          <Table.Summary.Cell align="center">
                            <Text strong={true}>{totalAmount}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="center">
                            <Text strong={true}>€{formatPrice(totalDiscount)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong={true}>€{formatPrice(order.totalPrice)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    )
                  );
                }}
              />
            </div>
            <Button className={"mt-4"} icon={<DownloadOutlined />} onClick={() => downloadInvoice(order)}>
              {t("download_invoice")}
            </Button>
          </Drawer>
        )}
        <OrderEditModal
          visible={editOrderModalVisible}
          order={orderToEdit}
          onClose={closeEditModal}
          updateOrder={updateOrder}
          refreshOrders={refreshOrders}
        />
        <div style={{ display: "none" }}>
          {isPrinting && (
            <>
              <OrderListTable
                ref={orderListRef}
                data={enrichOrdersWithProductData(
                  selectedRows.length > 0 ? selectedRows : getFilteredOrders(),
                  products,
                )}
                orderDetailsColumns={orderDetailsColumns}
                hasWeightProperty={hasWeightProperty}
                formatShippingMethod={formatShippingMethod}
              />
              <PickListTable
                ref={pickListRef}
                data={getPickListData(enrichOrdersWithProductData(getFilteredOrders(), products), products, categories)}
              />
              <PackingSlip
                ref={packingSlipRef}
                pickRouteIndex={pickRouteIndex}
                data={enrichOrdersWithProductData(getFilteredOrders(), products)}
              />
              <PackingSlip
                pickRouteIndex={pickRouteIndex}
                ref={packingSlipRefLandelijk}
                data={enrichOrdersWithProductData(getLandelijkOrders(), products)}
              />
              <PackingSlip
                pickRouteIndex={pickRouteIndex}
                ref={packingSlipRefRegionaal}
                data={enrichOrdersWithProductData(getRegionaalOrders(), products)}
              />
              <PackingSlip
                pickRouteIndex={pickRouteIndex}
                ref={packingSlipRefVceLandelijk}
                data={enrichOrdersWithProductData(getVceLandelijkOrders(), products)}
              />
              <PackingSlip
                pickRouteIndex={pickRouteIndex}
                ref={packingSlipRefHvoLandelijk}
                data={enrichOrdersWithProductData(getHvoOrders(), products)}
              />
              <SendLabel
                ref={sendListRef}
                data={getSendListData(enrichOrdersWithProductData(getFilteredOrders(), products))}
              />
              <SendLabel
                ref={sendListRefLandelijk}
                data={getSendListData(enrichOrdersWithProductData(getLandelijkOrders(), products))}
              />
              <SendLabel
                ref={sendListRefRegionaal}
                data={getSendListData(enrichOrdersWithProductData(getRegionaalOrders(), products))}
              />
              <SendLabel
                ref={sendListRefVceLandelijk}
                data={getSendListData(enrichOrdersWithProductData(getVceLandelijkOrders(), products))}
              />
              <SendLabel
                ref={sendListRefHvoLandelijk}
                data={getSendListData(enrichOrdersWithProductData(getHvoOrders(), products))}
              />
              <CombinedPackingSend
                ref={combinedPackingSendRef}
                data={enrichOrdersWithProductData(getFilteredOrders(), products)}
              />
              <CombinedPackingSend
                ref={combinedPackingSendRefLandelijk}
                data={enrichOrdersWithProductData(getLandelijkOrders(), products)}
              />
              <CombinedPackingSend
                ref={combinedPackingSendRefRegionaal}
                data={enrichOrdersWithProductData(getRegionaalOrders(), products)}
              />
              <CombinedPackingSend
                ref={combinedPackingSendRefVceLandelijk}
                data={enrichOrdersWithProductData(getVceLandelijkOrders(), products)}
              />
              <CombinedPackingSend
                ref={combinedPackingSendRefHvoLandelijk}
                data={enrichOrdersWithProductData(getHvoOrders(), products)}
              />
            </>
          )}
        </div>
      </Card>
    </>
  );
};

const DateWarningMessage = styled.p`
  color: #ff7300;
`;

export default Orders;
